import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState, createContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useMsg } from "../hooks/MsgProvider";
import useInformation from "hooks/useInformation";
import { createTheme } from "@mui/material/styles";
import useSysParam from "hooks/UseSysParam";
import ResetPwd from "view/pc/forgotPsw/ResetPwd.pc";
import ResetPwdMobile from "view/mobile/forgotPsw/ResetPwd.mobile";
import RenderPcOrMobile from "./RenderPcOrMobile";
import { forgetResetPwd, getLoginiconsInfo } from "api/index";
import { useHistory, useLocation } from "react-router-dom";
import { isAfter } from "date-fns";
import { rsaOaepEncrypt, rsaOaepDecrypt } from "utils/encrypt";

export const LoginViewContext = createContext({});

const schema = yup.object().shape({
	email: yup.string().trim().required(),
	pwd: yup.string().trim().required("Password is a required field"),
	confirmPassword: yup.string().trim().required("Confirm Password is a required field"),
});

const theme = (themes) =>
	createTheme({
		...themes,
		components: {
			...themes.components,
			MuiInputBase: {
				styleOverrides: {
					input: {
						padding: 8,
					},
				},
			},
		},
	});

function ResetPwdNew(props) {
	const uLocation = useLocation();
	const history = useHistory();
	const searchParams = new URLSearchParams(uLocation.search);
	const emailAddress = searchParams.get("email");
	const expireKey = searchParams.get("key");
	const [decryptedEmail, setDecryptedEmail] = useState("");

	const [showPassword, setShowPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const { setSuccessMsg, setErrMsg } = useMsg();
	const [showLockTip, setShowLockTip] = useState(false);
	const [lockTipContent, setLockTipContent] = useState("");
	const [backgroundImgList, setBackgroundImgList] = useState([]);
	const [activeBackgroundIndex, setActiveBackgroundIndex] = useState(0);
	const intervalRef = React.useRef();
	const { paramTextValue: logoLink } = useSysParam("sys_logo_hyperlink", true);
	const { paramIntValue: noticeHeight } = useSysParam("important_notice_box_height", true);
	const { paramIntValue: inforHeight } = useSysParam("useful_information_height", true);
	const noticeHeightPx = `${noticeHeight}px`;
	const inforHeightPx = `${inforHeight}px`;
	const [loginRemainTime, setLoginRemainTime] = useState(0);
	const loginIntervalRef = React.useRef();
	const [displayMode, setDisplayMode] = useState("login");
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		register,
		handleSubmit,
		control,
		setValue,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		resolver: yupResolver(schema),
		defaultValues: {
			email: "",
			pwd: "",
			confirmPassword: "",
		},
	});

	// decrypt query params safely and populate form
	useEffect(() => {
		const decodeAndSet = async () => {
			if (!emailAddress || !expireKey) {
				return;
			}
			try {
				const decodeEmailAddress = decodeURIComponent(emailAddress);
				const email = await rsaOaepDecrypt(decodeEmailAddress);
				setDecryptedEmail(email);
				setValue("email", email);
			} catch (e) {
				console.error("Failed to decrypt email from URL:", e);
				setShowLockTip(true);
				setLockTipContent("Invalid or corrupted reset link. Please request a new link.");
			}
		};
		decodeAndSet();
	}, [emailAddress, expireKey]);

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	const handleChangeDisplay = (event, name) => {
		event.preventDefault();
		setDisplayMode(name);
	};

	const handleClickShowNewPassword = () => {
		setShowNewPassword(!showNewPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const newData = {
				plainPassword: await rsaOaepEncrypt(data.pwd),
				confirmPlainPassword: await rsaOaepEncrypt(data.confirmPassword),
			};
			const res = await forgetResetPwd({
				userEmail: data.email,
				plainPassword: newData.plainPassword,
				confirmPlainPassword: newData.confirmPlainPassword,
			});
			setSuccessMsg(res?.data);
			setLockTipContent(res?.data);
			setShowLockTip(true);
		} catch (err) {
			setErrMsg(err);
		} finally {
			setIsLoading(false);
		}
	};

	const usefulInfo = useInformation("USEFUL_INFO", true).informations;
	const iptNotice = useInformation("IPT_NOTICE", true).informations;

	const getBackgroundImage = React.useCallback(() => {
		getLoginiconsInfo()
			.then((res) => {
				const imgsData = res.data.icons;
				setBackgroundImgList(imgsData);
				if (Array.isArray(imgsData) && imgsData.length > 0) {
					setActiveBackgroundIndex(0);
					clearInterval(intervalRef.current);
					intervalRef.current = setInterval(
						() => {
							setActiveBackgroundIndex((prev) => {
								if (imgsData.length <= prev + 1) {
									return 0;
								}
								return prev + 1;
							});
						},
						res.data.intervalTime * 1000 || 10000
					);
				}
			})
			.catch((err) => {
				if (err) {
					console.log(err);
				}
			});
	}, [backgroundImgList.length]);

	useEffect(() => {
		getBackgroundImage();
	}, [getBackgroundImage]);

	useEffect(() => {
		if (loginRemainTime === 5) {
			clearInterval(loginIntervalRef.current);
			loginIntervalRef.current = setInterval(() => {
				setLoginRemainTime((prev) => prev - 1);
			}, 1000);
		}
		if (loginRemainTime === 0) {
			clearInterval(loginIntervalRef.current);
		}
	}, [loginRemainTime]);

	const handleExpire = async () => {
		try {
			if (!expireKey) {
				return;
			}
			const decodeKey = decodeURIComponent(expireKey);
			const expireTime = await rsaOaepDecrypt(decodeKey);
			if (isAfter(new Date(), new Date(Number(expireTime)))) {
				setLockTipContent("Your link has been expired, please try again.");
				setShowLockTip(true);
			}
		} catch (e) {
			console.error("Failed to decrypt or parse expire key:", e);
			setShowLockTip(true);
			setLockTipContent("Invalid or corrupted reset link. Please request a new link.");
		}
	};

	useEffect(() => {
		handleExpire();
	}, []);

	const handleLockClose = () => {
		setShowLockTip(false);
		history.replace("/login");
	};

	return (
		<LoginViewContext.Provider
			value={{
				register,
				isLoading,
				theme,
				backgroundImgList,
				noticeHeightPx,
				iptNotice,
				inforHeightPx,
				usefulInfo,
				activeBackgroundIndex,
				logoLink,
				handleSubmit,
				onSubmit,
				control,
				errors,
				handleClickShowPassword,
				handleMouseDownPassword,
				showPassword,
				showLockTip,
				setShowLockTip,
				lockTipContent,
				setErrMsg,
				isError,
				loginRemainTime,
				displayMode,
				handleChangeDisplay,

				showConfirmPassword,
				handleClickShowConfirmPassword,
				showNewPassword,
				handleClickShowNewPassword,
				handleLockClose,
			}}
		>
			<RenderPcOrMobile pc={<ResetPwd />} mobile={<ResetPwdMobile />} />
		</LoginViewContext.Provider>
	);
}
export default ResetPwdNew;
